import {FilterValidator} from "./utils/validation";
import {makeHumanReadableComponent, trFilter, trParams} from "./utils/translation";
import {i18n} from "./utils/i18n";

/**
 * Enum for filter operator types
 * @readonly
 * @enum {string}
 */
export const operatorType = {
    groupOperators: "group",
    fulltextOperators: "fulltext",
    unaryOperators: "unary",
    binaryOperators: "binary",
    ternaryOperators: "ternary",
    arrayOperators: "array"
}

/**
 * Definitions of filter operator types
 * @todo override **list** and **builder** for each type in your implementation
 */
export const operatorTypes = {
    [operatorType.groupOperators]: {
        type: operatorType.groupOperators,
        list: [],
        validation: [
            FilterValidator.isArray
        ],
        builder: (key, value) => window.console.error('Implement groupOperators builder : (key, value) => {}'),
        render: (langPath, key, value, renderValues, filters) => {
            const parts = value.map(nested => makeHumanReadableComponent(langPath, nested, renderValues, filters));
            const result = parts.join(' ' + trFilter(key) + ' ');
            return parts.length > 1 ? ` (${result}) ` : result;
        },
        convertableToTypes: []
    },

    [operatorType.fulltextOperators]: {
        type: operatorType.fulltextOperators,
        list: [],
        validation: [
            FilterValidator.isString
        ],
        builder: (key, value) => window.console.error('Implement fulltextOperators builder : (key, value) => {}'),
        render: (langPath, key, value) => trFilter(key, [i18n.t(langPath + value + '.name')]),
        convertableToTypes: []
    },

    [operatorType.unaryOperators]: {
        type: operatorType.unaryOperators,
        list: [],
        validation: [
            FilterValidator.isString
        ],
        builder: (key, value) => window.console.error('Implement unaryOperators builder : (key, value) => {}'),
        render: (langPath, key, value) => trFilter(key, [i18n.t(langPath + value + '.name')]),
        convertableToTypes: []
    },

    [operatorType.binaryOperators]: {
        type: operatorType.binaryOperators,
        list: [],
        validation: [
            FilterValidator.isObject,
            FilterValidator.isSingleKey
        ],
        builder: (key, value) => window.console.error('Implement binaryOperators builder : (key, value) => {}'),
        render: (langPath, key, value, renderValues) => {
            const property = Object.keys(value)[0];
            return trFilter(key, trParams(langPath + property, [
                'name',
                value[property]
            ], renderValues));
        },
        convertableToTypes: [operatorType.binaryOperators, operatorType.ternaryOperators, operatorType.arrayOperators]
    },

    [operatorType.ternaryOperators]: {
        type: operatorType.ternaryOperators,
        list: [],
        validation: [
            FilterValidator.isObject,
            FilterValidator.isSingleKey,
            FilterValidator.isArrayInFirstKey,
            FilterValidator.arrayInFirstKeyHasTwoValues
        ],
        builder: (key, value) => window.console.error('Implement ternaryOperators builder : (key, value) => {}'),
        render: (langPath, key, value, renderValues) => {
            const property = Object.keys(value)[0];
            return trFilter(key, trParams(langPath + property, [
                'name',
                value[property][0],
                value[property][1]
            ], renderValues));
        },
        convertableToTypes: [operatorType.binaryOperators, operatorType.ternaryOperators, operatorType.arrayOperators]
    },

    [operatorType.arrayOperators]: {
        type: operatorType.arrayOperators,
        list: [],
        validation: [
            FilterValidator.isObject,
            FilterValidator.isSingleKey,
            FilterValidator.isArrayInFirstKey
        ],
        builder: (key, value) => window.console.error('Implement arrayOperators builder : (key, value) => {}'),
        render: (langPath, key, value, renderValues) => {
            const property = Object.keys(value)[0];
            return trFilter(key, [
                ...trParams(langPath + property, ['name'], renderValues),
                trParams(langPath + property, value[property], renderValues).join(', ')
            ]);
        },
        convertableToTypes: [operatorType.binaryOperators, operatorType.ternaryOperators, operatorType.arrayOperators]
    }
};

/**
 * Returns filter component from filter object
 * - uses filters.getType from passed implementation
 * - what is returned depends on your implementation (what builder methods return)
 * @param filterObject {Object}
 * @param filters {Object}
 * @returns {*}
 * @recursive
 */
export const makeFilterComponent = (filterObject, filters) => {
    const key = Object.keys(filterObject)[0];
    const type = filters.getType(key);
    FilterValidator.validate(filterObject[key], type.validation);
    return type.builder(key, filterObject[key]);
};

export const AbstractFilters = {

    /**
     * Describes filter implementation type
     * @type {FilterType}
     */
    type: undefined,

    /**
     * @param sortObject {Object}
     * @abstract
     */
    makeSort: (sortObject) => window.console.error('Implement makeSort : (sortObject) => {}'),

    /**
     * @param langPath {string}
     * @param sortObject {Object}
     * @abstract
     */
    makeHumanReadableSort: (langPath, sortObject) => window.console.error('Implement makeHumanReadableSort : (langPath, sortObject) => {}'),

    /**
     * @param filterObject {Object}
     * @abstract
     */
    makeFilter: (filterObject) => window.console.error('Implement makeFilter : (filterObject) => {}'),

    /**
     * @param filterObject {Object}
     * @abstract
     */
    makeSimpleFilter: (filterObject) => window.console.error('Implement makeSimpleFilter : (filterObject) => {}'),

    /**
     * @param langPath {string}
     * @param filterObject {Object}
     * @param renderValues {Object}
     * @param filters {Object}
     * @param filterOP {Object}
     * @return {string}
     */
    makeHumanReadable: (langPath, filterObject, renderValues, filters, filterOP) => {
        if (Array.isArray(filterObject)) { // implicit AND
            return filterObject.map(nested => makeHumanReadableComponent(langPath, nested, renderValues, filters)).join(' ' + trFilter(filterOP.AND) + ' ');
        } else {
            return makeHumanReadableComponent(langPath, filterObject, renderValues, filters)
                .trim()
                .replace(/(^\(|\)$)/g, '');
        }
    },

    /**
     * @param key {string}
     * @param operatorTypes {Object}
     * @return {Object}
     */
    getType: (key, operatorTypes) => {
        for (const type of Object.values(operatorTypes)) {
            if (type.list.includes(key)) {
                return type;
            }
        }
        window.console.warn('Invalid filter syntax, expected object: `{operator: value}');
    },

    /**
     * @param filterOP {string}
     * @param filterDataType {string}
     * @param filterDataTypeAllowedOperations {Object}
     * @returns boolean
     */
    isFilterOPAllowedForDataType: (filterOP, filterDataType, filterDataTypeAllowedOperations) => {
        return filterDataTypeAllowedOperations[filterDataType].includes(filterOP);
    },

    /**
     * Converts value from one operator type to another
     * @param curType {Object} current operator type
     * @param newType {Object} new operator type
     * @param curValue {*} current value
     * @param defaultValue {*} default value for new operator type
     * @returns {[*,*]| [*] | *}
     */
    convertValue: (curType, newType, curValue, defaultValue) => {
        const conversionType = [curType.type, '->', newType.type].join('');
        switch (conversionType) {
            case 'binary->ternary':
                return [curValue, defaultValue];
            case 'binary->array':
                return [curValue];
            case 'ternary->binary':
            case 'array->binary':
                return curValue[0];
            case 'ternary->array':
                if (curValue[0] === curValue[1]) {
                    return [curValue[0]];
                } else {
                    return curValue;
                }
            case 'array->ternary':
                if (curValue.length > 2) {
                    return curValue.slice(0, 2);
                } else if (curValue.length === 2) {
                    return curValue;
                } else {
                    return [curValue[0], defaultValue];
                }
            default:
                return curValue;
        }
    },

    /**
     * Checks if value can be converted from one operator type to another
     * @param curType {Object} current operator type
     * @param newType {Object} new operator type
     * @returns {boolean}
     */
    isValueConvertable: (curType, newType) => {
        return curType.convertableToTypes.includes(newType.type);
    },
};
